import { AnyAction, createSlice, current } from '@reduxjs/toolkit';
import type {
  CreateLegalCodeDto,
  ItemRo,
  TaskRo,
} from '../services/api.generated';
import { api } from '../services/api';
import { logoutAction } from './authSlice';
import { readJson } from 'src/utils/safeStorage';
import {
  UncertainRegionDecisionRo,
  UncertainRegionRo,
} from 'src/utils/uncertainRegions';

/**
 * Writes that change the redaction decisions of a task. After one, the
 * backend marks a finished export outdated (tasks/export-stale.ts); the editor
 * flips its copy too, so it stops offering the cached link to the old file.
 */
const REDACTION_WRITE_ENDPOINTS = [
  'tasksControllerAddItem',
  'tasksControllerAddCustomBox',
  'tasksControllerUpdateBox',
  'tasksControllerChangeRedactState',
  'tasksControllerDeleteItem',
  'tasksControllerCreateLegalCode',
  'tasksControllerRemoveLegalCode',
  'tasksControllerRedactAll',
  'tasksControllerUnRedactAll',
  'tasksControllerChangeItemsRedactState',
  'tasksRemovePageSuggestions',
  'tasksRestorePageSuggestions',
  'uncertainRegionsRedact',
  'uncertainRegionsReopen',
  'uncertainRegionsBulk',
] as const;

// Looked up per action, not at import: tests mock the api module partly.
const redactionWriteFulfilled = (action: AnyAction): boolean =>
  REDACTION_WRITE_ENDPOINTS.some((name) =>
    api.endpoints[name].matchFulfilled(action)
  );

/** taskId on the args or on their one DTO; some DTOs leave it optional. */
export function redactionWriteTaskId(args: unknown): string | undefined {
  if (!args || typeof args !== 'object') return undefined;
  const direct = (args as { taskId?: unknown }).taskId;
  if (typeof direct === 'string') return direct;
  for (const value of Object.values(args)) {
    const nested = (value as { taskId?: unknown } | null)?.taskId;
    if (typeof nested === 'string') return nested;
  }
  return undefined;
}

export type EditPageUndoAction =
  | {
      type: 'CHANGE_REDACT_STATE';
      taskId: string;
      itemId: string;
      isRedact: boolean;
    }
  | {
      type: 'DELETE_ITEM';
      taskId: string;
      itemId: string;
    }
  | {
      type: 'ADD_ITEM';
      taskId: string;
      pageNumber: number;
      boxesId: string[];
    }
  | {
      type: 'ADD_CUSTOM_BOX';
      taskId: string;
      pageNumber: number;
      rect: any[];
    }
  | {
      type: 'SET_LEGAL_CODE';
      taskId: string;
      itemId: string;
      legalCode: CreateLegalCodeDto['legalCode'];
    }
  | {
      /** "Not sure" decision (single or bulk): undo reopens and removes items. */
      type: 'UNCERTAIN_DECISION';
      taskId: string;
      markerIds: string[];
      createdItemIds: string[];
    };

/**
 * "Redact word": the next word selection is limited to the words inside one
 * marker and completes it.
 */
export type PartialMarkerState = {
  markerId: string;
  pageNumber: number;
};

/** Popover of a "not sure" box: which marker, where on screen (viewport px). */
export type MarkerPopoverState = { markerId: string; x: number; y: number };

/**
 * The items one "remove all suggestions on this page" took out, kept so its
 * undo can put exactly these back into the editor.
 */
export type SuggestionRemovalState = {
  taskId: string;
  batchId: string;
  pageNumber: number;
  items: ItemRo[];
};

type EditPageSliceType = {
  /**
   * Document id from the /edit/:id route. Everything below belongs to this
   * document; responses for any other id are dropped (late or cached ones).
   */
  routeTaskId: string | null;
  task: TaskRo | null;
  pages: ItemRo[][];
  openRightSidebar: boolean;
  undoStack: EditPageUndoAction[];
  redoStack: EditPageUndoAction[];
  activeUndoAction: EditPageUndoAction | null;
  activeRedoAction: EditPageUndoAction | null;
  /** "Not sure" markers of the routed document. */
  markers: UncertainRegionRo[];
  markersEnabled: boolean;
  markersLegacy: boolean;
  markersLoaded: boolean;
  selectedMarkerId: string | null;
  markerPopover: MarkerPopoverState | null;
  showReviewedMarkers: boolean;
  partialMarker: PartialMarkerState | null;
  /**
   * Every removal whose undo may still come, by batch id. Undo of an earlier
   * batch after a later removal must still put its items back.
   */
  suggestionRemovals: Record<string, SuggestionRemovalState>;
};

const localSettings = readJson<{ openRightSidebar?: unknown }>(
  'appSettings',
  {},
  (value): value is { openRightSidebar?: unknown } => !!value && typeof value === 'object'
);
const maxUndoStackLength = 50;

const initialState: EditPageSliceType = {
  routeTaskId: null,
  task: null,
  pages: [],
  openRightSidebar:
    typeof localSettings.openRightSidebar === 'boolean' ? localSettings.openRightSidebar : true,
  undoStack: [],
  redoStack: [],
  activeUndoAction: null,
  activeRedoAction: null,
  markers: [],
  markersEnabled: false,
  markersLegacy: false,
  markersLoaded: false,
  selectedMarkerId: null,
  markerPopover: null,
  showReviewedMarkers: false,
  partialMarker: null,
  suggestionRemovals: {},
};

const resetDocumentState = (state: EditPageSliceType) => {
  state.task = null;
  state.pages = [];
  state.undoStack = [];
  state.redoStack = [];
  state.activeUndoAction = null;
  state.activeRedoAction = null;
  state.markers = [];
  state.markersEnabled = false;
  state.markersLegacy = false;
  state.markersLoaded = false;
  state.selectedMarkerId = null;
  state.markerPopover = null;
  state.partialMarker = null;
  state.suggestionRemovals = {};
};

const upsertMarkers = (
  state: EditPageSliceType,
  markers: UncertainRegionRo[]
) => {
  for (const marker of markers) {
    const index = state.markers.findIndex((m) => m.id === marker.id);
    if (index > -1) state.markers[index] = marker;
    else state.markers.push(marker);
  }
};

const removePageItems = (state: EditPageSliceType, itemIds: string[]) => {
  const ids = new Set(itemIds);
  state.pages.forEach((page) => {
    if (!page) return;
    for (let i = page.length - 1; i >= 0; i--) {
      if (ids.has(page[i].id)) page.splice(i, 1);
    }
  });
};

/** True when a request was made for a document other than the routed one. */
const isOtherTask = (state: EditPageSliceType, taskId?: string | null) =>
  !state.routeTaskId || (!!taskId && taskId !== state.routeTaskId);

const legalActionHandler = (action: AnyAction) => {
  return (
    api.endpoints.tasksControllerCreateLegalCode.matchFulfilled(action) ||
    api.endpoints.tasksControllerRemoveLegalCode.matchFulfilled(action)
  );
};

const fixRect = (rect: number[][]) => {
  const res = rect.sort((p1, p2) => (p1[0] - p1[1] >= p2[0] - p2[1] ? 1 : -1));
  const topLeft = res[0];
  const bottomRight = res[3];

  const res2 = rect.sort((p1, p2) => (p1[0] + p1[1] >= p2[0] + p2[1] ? 1 : -1));
  const topRight = res2[3];
  const bottomLeft = res2[0];

  return [topLeft, topRight, bottomLeft, bottomRight];
};

const fixItem = (item: ItemRo) => ({
  ...item,
  boxes: item.boxes.map((box) => ({
    ...box,
    rect: fixRect(box.rect),
  })),
});

const findPageItem = (
  pages: EditPageSliceType['pages'],
  itemId: string
) => {
  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    const itemIndex = pages[pageIndex]?.findIndex((item) => item.id === itemId);
    if (itemIndex > -1) {
      return { pageIndex, itemIndex, item: pages[pageIndex][itemIndex] };
    }
  }

  return null;
};

const pushHistoryAction = (
  stack: EditPageUndoAction[],
  historyAction: EditPageUndoAction
) => {
  stack.push(historyAction);
  if (stack.length > maxUndoStackLength) {
    stack.shift();
  }
};

const recordHistoryAction = (
  state: EditPageSliceType,
  historyAction: EditPageUndoAction | null
) => {
  if (!historyAction) {
    return;
  }

  if (state.activeUndoAction) {
    pushHistoryAction(state.redoStack, historyAction);
    return;
  }

  if (state.activeRedoAction) {
    pushHistoryAction(state.undoStack, historyAction);
    return;
  }

  pushHistoryAction(state.undoStack, historyAction);
  state.redoStack = [];
};

const editPageSlice = createSlice({
  name: 'editPage',
  initialState,
  reducers: {
    // The edit page dispatches these from its route param. Opening another
    // document drops the previous one's task, pages and undo history.
    openTask: (state, { payload }: { payload: string }) => {
      if (
        state.routeTaskId !== payload ||
        (state.task && state.task.id !== payload)
      ) {
        resetDocumentState(state);
      }
      state.routeTaskId = payload;
    },
    closeTask: (state, { payload }: { payload: string }) => {
      if (state.routeTaskId === payload) {
        resetDocumentState(state);
        state.routeTaskId = null;
      }
    },
    // A cached getTask result fires no fulfilled action, so the page hands
    // the routed task over explicitly.
    syncTask: (state, { payload }: { payload: TaskRo }) => {
      if (payload.id === state.routeTaskId && state.task?.id !== payload.id) {
        state.task = payload;
      }
    },
    changeRightSidebarState: (state, { payload }: { payload: boolean }) => {
      localStorage.setItem(
        'appSettings',
        JSON.stringify({
          openRightSidebar: payload,
        })
      );
      state.openRightSidebar = payload;
    },
    beginUndo: (state) => {
      state.activeUndoAction = state.undoStack.pop() || null;
    },
    completeUndo: (state) => {
      state.activeUndoAction = null;
    },
    cancelUndo: (state) => {
      if (state.activeUndoAction) {
        state.undoStack.push(state.activeUndoAction);
      }
      state.activeUndoAction = null;
    },
    beginRedo: (state) => {
      state.activeRedoAction = state.redoStack.pop() || null;
    },
    completeRedo: (state) => {
      state.activeRedoAction = null;
    },
    cancelRedo: (state) => {
      if (state.activeRedoAction) {
        state.redoStack.push(state.activeRedoAction);
      }
      state.activeRedoAction = null;
    },
    selectMarker: (state, { payload }: { payload: string | null }) => {
      state.selectedMarkerId = payload;
    },
    openMarkerPopover: (state, { payload }: { payload: MarkerPopoverState }) => {
      state.markerPopover = payload;
      state.selectedMarkerId = payload.markerId;
    },
    closeMarkerPopover: (state) => {
      state.markerPopover = null;
    },
    startPartialMarker: (
      state,
      { payload }: { payload: { markerId: string; pageNumber: number } }
    ) => {
      state.partialMarker = { ...payload };
      state.selectedMarkerId = payload.markerId;
    },
    endPartialMarker: (state) => {
      state.partialMarker = null;
    },
  },
  extraReducers: ({ addMatcher, addCase }) => {
    // ========================= LOGOUT ========================= //
    addCase(logoutAction, () => {
      localStorage.removeItem('appSettings');
      return initialState;
    });
    addMatcher(
      api.endpoints.tasksControllerGetTask.matchFulfilled,
      (state, { payload }) => {
        // Late response for a document the user already left.
        if (payload.id !== state.routeTaskId) return;
        if (state.task && state.task.id !== payload.id) {
          resetDocumentState(state);
        }
        state.task = payload;
      }
    );
    addMatcher(
      api.endpoints.uncertainRegionsList.matchFulfilled,
      (state, { payload, meta }) => {
        if (meta.arg.originalArgs.taskId !== state.routeTaskId) return;
        state.markers = payload.markers;
        state.markersEnabled = payload.enabled;
        state.markersLegacy = payload.legacy;
        state.markersLoaded = true;
      }
    );
    const decisionMatchers = [
      api.endpoints.uncertainRegionsRedact,
      api.endpoints.uncertainRegionsComplete,
      api.endpoints.uncertainRegionsDismiss,
      api.endpoints.uncertainRegionsBulk,
    ];
    addMatcher(
      (action: AnyAction) =>
        decisionMatchers.some((endpoint) => endpoint.matchFulfilled(action)),
      (state, action: AnyAction) => {
        const taskId: string = action.meta.arg.originalArgs.taskId;
        if (taskId !== state.routeTaskId) return;
        const payload = action.payload as UncertainRegionDecisionRo;
        if (!payload.markers.length) return;
        upsertMarkers(state, payload.markers);
        for (const item of payload.items) {
          const pageNumber = payload.markers.find((m) =>
            m.resultItemIds.includes(item.id)
          )?.pageNumber;
          if (pageNumber == null) continue;
          if (!state.pages[pageNumber]) state.pages[pageNumber] = [];
          state.pages[pageNumber].push(fixItem(item));
        }
        // "Redact part": the drawn boxes are undone with the decision, not
        // one by one (the marker owns them now).
        const drawn = new Set(
          api.endpoints.uncertainRegionsComplete.matchFulfilled(action)
            ? (action.meta.arg.originalArgs.itemIds as string[])
            : []
        );
        if (drawn.size) {
          state.undoStack = state.undoStack.filter(
            (entry) => !(entry.type === 'DELETE_ITEM' && drawn.has(entry.itemId))
          );
          state.partialMarker = null;
        }
        state.markerPopover = null;
        recordHistoryAction(state, {
          type: 'UNCERTAIN_DECISION',
          taskId,
          markerIds: payload.markers.map((m) => m.id),
          createdItemIds: payload.markers.flatMap((m) => m.resultItemIds),
        });
      }
    );
    addMatcher(
      api.endpoints.uncertainRegionsReopen.matchFulfilled,
      (state, { payload, meta }) => {
        const { taskId, removeItems } = meta.arg.originalArgs;
        if (taskId !== state.routeTaskId) return;
        if (removeItems) {
          const before = new Map(state.markers.map((m) => [m.id, m]));
          removePageItems(
            state,
            payload.markers.flatMap((m) => before.get(m.id)?.resultItemIds ?? [])
          );
        }
        // Undo only; there is no redo for marker decisions.
        upsertMarkers(state, payload.markers);
      }
    );
    addMatcher(
      api.endpoints.tasksRemovePageSuggestions.matchFulfilled,
      (state, { payload, meta }) => {
        const { taskId, pageNumber } = meta.arg.originalArgs;
        if (taskId !== state.routeTaskId || !payload.itemIds.length) return;
        const ids = new Set(payload.itemIds);
        const removed = (current(state).pages[pageNumber] || []).filter(
          (item) => ids.has(item.id)
        );
        state.suggestionRemovals[payload.batchId] = {
          taskId,
          batchId: payload.batchId,
          pageNumber,
          items: removed,
        };
        removePageItems(state, payload.itemIds);
      }
    );
    addMatcher(
      api.endpoints.tasksRestorePageSuggestions.matchFulfilled,
      (state, { payload, meta }) => {
        const { taskId, batchId } = meta.arg.originalArgs;
        const removal = state.suggestionRemovals[batchId];
        if (
          taskId !== state.routeTaskId ||
          !removal ||
          removal.taskId !== taskId
        ) {
          return;
        }
        const ids = new Set(payload.itemIds);
        if (!state.pages[removal.pageNumber]) {
          state.pages[removal.pageNumber] = [];
        }
        const page = state.pages[removal.pageNumber];
        for (const item of removal.items) {
          if (ids.has(item.id) && !page.some((it) => it.id === item.id)) {
            page.push(item);
          }
        }
        delete state.suggestionRemovals[batchId];
      }
    );
    addMatcher(
      api.endpoints.tasksControllerSetAssignee.matchFulfilled,
      (state, { payload }) => {
        if (state.task && state.task.id === payload.id) {
          state.task.assigneeId = payload.assigneeId ?? null;
        }
      }
    );
    addMatcher(
      api.endpoints.tasksControllerGetPagesItems.matchFulfilled,
      (state, { payload, meta }) => {
        if (meta.arg.originalArgs.taskId !== state.routeTaskId) return;
        for (let i = 0; i < payload.length; i++) {
          state.pages[i + meta.arg.originalArgs.start] =
            payload[i].items.map(fixItem);
        }
      }
    );
    addMatcher(
      api.endpoints.tasksControllerChangeRedactState.matchFulfilled,
      (state, { meta }) => {
        if (isOtherTask(state, meta.arg.originalArgs.changeRedactStateDto.taskId))
          return;
        const redactedItemId =
          meta.arg.originalArgs.changeRedactStateDto.itemId;
        const currentRedactStatus =
          meta.arg.originalArgs.changeRedactStateDto.isRedact;
        const foundItem = findPageItem(state.pages, redactedItemId);
        if (!foundItem) return;

        const { item, pageIndex, itemIndex } = foundItem;
        if (item.redact !== currentRedactStatus) {
          const boxesId = item.boxes
            .map((box) => box.id)
            .filter((id): id is string => !!id);
          const rect = item.boxes[0]?.rect;
          const hasTextBoxes = item.boxes.some((box) => !!box.text);
          recordHistoryAction(
            state,
            !currentRedactStatus && item.custom && state.task
              ? hasTextBoxes && boxesId.length
                ? {
                    type: 'ADD_ITEM',
                    taskId: state.task.id,
                    pageNumber: pageIndex,
                    boxesId,
                  }
                : rect
                ? {
                    type: 'ADD_CUSTOM_BOX',
                    taskId: state.task.id,
                    pageNumber: pageIndex,
                    rect,
                  }
                : null
              : state.routeTaskId
              ? {
                  type: 'CHANGE_REDACT_STATE',
                  taskId: state.routeTaskId,
                  itemId: redactedItemId,
                  isRedact: item.redact,
                }
              : null
          );
        }

        if (!currentRedactStatus && item.custom) {
          state.pages[pageIndex].splice(itemIndex, 1);
        } else {
          state.pages[pageIndex][itemIndex].redact = currentRedactStatus;
        }
      }
    );
    addMatcher(
      api.endpoints.tasksControllerDeleteItem.matchFulfilled,
      (state, { meta }) => {
        if (isOtherTask(state, meta.arg.originalArgs.deleteItemDto.taskId))
          return;
        const deletedItemId = meta.arg.originalArgs.deleteItemDto.id;
        const foundItem = findPageItem(state.pages, deletedItemId);
        if (!foundItem) return;

        const { item, pageIndex, itemIndex } = foundItem;
        if (item.custom && state.task) {
          const boxesId = item.boxes
            .map((box) => box.id)
            .filter((id): id is string => !!id);
          const rect = item.boxes[0]?.rect;
          const hasTextBoxes = item.boxes.some((box) => !!box.text);
          recordHistoryAction(
            state,
            hasTextBoxes && boxesId.length
              ? {
                  type: 'ADD_ITEM',
                  taskId: state.task.id,
                  pageNumber: pageIndex,
                  boxesId,
                }
              : rect
              ? {
                  type: 'ADD_CUSTOM_BOX',
                  taskId: state.task.id,
                  pageNumber: pageIndex,
                  rect,
                }
              : null
          );
        } else if (item.redact && state.routeTaskId) {
          recordHistoryAction(state, {
            type: 'CHANGE_REDACT_STATE',
            taskId: state.routeTaskId,
            itemId: deletedItemId,
            isRedact: true,
          });
        }

        if (item.custom) {
          state.pages[pageIndex].splice(itemIndex, 1);
        } else {
          state.pages[pageIndex][itemIndex].redact = false;
        }
      }
    );
    addMatcher(
      api.endpoints.tasksControllerAddCustomBox.matchFulfilled,
      (state, { payload, meta }) => {
        const { taskId, pageNumber } = meta.arg.originalArgs.addCustomBoxDto;
        if (taskId !== state.routeTaskId) return;
        recordHistoryAction(state, {
          type: 'DELETE_ITEM',
          taskId,
          itemId: payload.id,
        });
        if (!state.pages[pageNumber]) {
          state.pages[pageNumber] = [];
        }
        state.pages[pageNumber].push(fixItem(payload));
      }
    );
    addMatcher(
      api.endpoints.tasksControllerAddItem.matchFulfilled,
      (state, { payload, meta }) => {
        const { taskId, pageNumber } = meta.arg.originalArgs.addItemDto;
        if (taskId !== state.routeTaskId) return;
        recordHistoryAction(state, {
          type: 'DELETE_ITEM',
          taskId,
          itemId: payload.id,
        });
        if (!state.pages[pageNumber]) {
          state.pages[pageNumber] = [];
        }
        state.pages[pageNumber].push(fixItem(payload));
      }
    );
    addMatcher(
      api.endpoints.tasksControllerUpdateBox.matchFulfilled,
      (state, { meta }) => {
        const { taskId, boxId, rect } = meta.arg.originalArgs.updateBoxDto;
        if (taskId !== state.routeTaskId) return;
        state.pages.forEach((page) => {
          if (!page) return;
          page.forEach((item) => {
            item.boxes?.forEach((box) => {
              if (box.id === boxId) {
                box.rect = fixRect(rect);
              }
            });
          });
        });
      }
    );
    addMatcher(legalActionHandler, (state, { meta }) => {
      const taskId =
        meta.arg.originalArgs.createLegalCodeDto?.taskId ||
        meta.arg.originalArgs.removeLegalCodeDto?.taskId;
      if (taskId !== state.routeTaskId) return;
      const itemId =
        meta.arg.originalArgs.createLegalCodeDto?.itemId ||
        meta.arg.originalArgs.removeLegalCodeDto?.itemId;
      const foundItem = findPageItem(state.pages, itemId);
      if (!foundItem) return;

      const { item, pageIndex, itemIndex } = foundItem;
      const newLegalCode =
        meta.arg.originalArgs.createLegalCodeDto?.legalCode || 'NONE';
      if (state.task && item.legalCode !== newLegalCode) {
        recordHistoryAction(state, {
          type: 'SET_LEGAL_CODE',
          taskId: state.task.id,
          itemId,
          legalCode: item.legalCode,
        });
      }
      state.pages[pageIndex][itemIndex].legalCode = newLegalCode;
    });
    addMatcher(
      api.endpoints.tasksControllerGetTaskOutputState.matchFulfilled,
      (state, { payload, meta }) => {
        if (state.task && state.task.id === meta.arg.originalArgs.id) {
          state.task.outputState = payload.outputState;
        }
      }
    );
    addMatcher(redactionWriteFulfilled, (state, action) => {
      const taskId =
        redactionWriteTaskId(action.meta?.arg?.originalArgs) ??
        state.routeTaskId;
      if (!state.task || taskId !== state.routeTaskId) return;
      if (state.task.id !== taskId) return;
      if (state.task.outputState === 'GENERATED_OUTPUT') {
        state.task.outputState = 'OUTDATED_OUTPUT';
      }
    });
    addMatcher(
      api.endpoints.tasksControllerGenerateOutput.matchFulfilled,
      (state, { meta }) => {
        if (
          state.task &&
          state.task.id === meta.arg.originalArgs.generateOutputDto.id
        ) {
          state.task.outputState = 'GENERATING_OUTPUT';
        }
      }
    );
    addMatcher(
      api.endpoints.tasksControllerChangeItemsRedactState.matchFulfilled,
      (state, { meta }) => {
        const { taskId, itemIds, isRedact } =
          meta.arg.originalArgs.itemsRedactStateDto;
        if (taskId !== state.routeTaskId) return;
        const idSet = new Set(itemIds as string[]);

        // forEach skips sparse holes; safe for decade-window pages
        state.pages.forEach((page) => {
          if (!page) return;
          for (let i = page.length - 1; i >= 0; i--) {
            const item = page[i];
            if (!idSet.has(item.id)) continue;
            if (!isRedact && item.custom) {
              page.splice(i, 1);
            } else {
              item.redact = isRedact;
            }
          }
        });
      }
    );
  },
});

export const {
  openTask: openEditPageTaskAction,
  closeTask: closeEditPageTaskAction,
  syncTask: syncEditPageTaskAction,
  changeRightSidebarState: changeRightSidebarStateAction,
  beginUndo: beginEditPageUndoAction,
  completeUndo: completeEditPageUndoAction,
  cancelUndo: cancelEditPageUndoAction,
  beginRedo: beginEditPageRedoAction,
  completeRedo: completeEditPageRedoAction,
  cancelRedo: cancelEditPageRedoAction,
  selectMarker: selectMarkerAction,
  openMarkerPopover: openMarkerPopoverAction,
  closeMarkerPopover: closeMarkerPopoverAction,
  startPartialMarker: startPartialMarkerAction,
  endPartialMarker: endPartialMarkerAction,
} = editPageSlice.actions;

export default editPageSlice.reducer;
